/**
 * The assistant's mark: the tab icon, in a circle. The favicon is the beer
 * itself — an amber tile, a scalloped cream head, a few bubbles, in solid
 * shapes — and the orb is that same drawing clipped round, so the assistant
 * reads as Beerwright's without looking like a second app tile. The sparkle is
 * everyone else's AI icon; this one is a pint.
 *
 * Flat on purpose, like the favicon: no sheen, rim or glint. At the 12–16 px it
 * is drawn beside a heading those turn to smudge, and solid shapes stay a beer.
 * The motion is the head bobbing and its edge rolling, and the bubbles rising.
 *
 * Sized like a lucide icon (24 viewBox, CSS `size-*`) so it drops in wherever
 * the sparkle used to sit. Colours are the favicon's literals, not
 * `currentColor`, because an orb that inherited the button's ink would stop
 * being beer.
 */

import { type SVGProps, useId } from 'react';

const BUBBLES = [
  { cx: 8.3, cy: 19.2, r: 1.25, delay: '0s' },
  { cx: 15.4, cy: 20.1, r: 1.05, delay: '0.45s' },
  { cx: 11.6, cy: 18.2, r: 0.9, delay: '0.9s' },
  { cx: 16.8, cy: 16.9, r: 1.25, delay: '1.3s' },
  { cx: 6.9, cy: 16.3, r: 0.8, delay: '1.7s' },
] as const;

/** The head: flat across the top, five scallops along its lower edge. */
export const ORB_HEAD_PATH =
  'M0 0 H24 V8.4 H22.6 Q20.5 11.1 18.4 8.4 Q16.3 11.1 14.2 8.4 Q12.1 11.1 10 8.4 Q7.9 11.1 5.8 8.4 Q3.7 11.1 1.4 8.4 H0 Z';

export function AssistantOrb({
  className,
  live = false,
  ...props
}: SVGProps<SVGSVGElement> & {
  /** Faster bubbles and a busier head — the drawer uses this while a turn is streaming. */
  readonly live?: boolean;
}) {
  const uid = useId().replace(/:/g, '');
  const clipId = `${uid}-clip`;
  const beerId = `${uid}-beer`;

  return (
    <svg
      data-slot="assistant-orb"
      data-live={live ? '' : undefined}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx="12" cy="12" r="10" />
        </clipPath>
        <linearGradient id={beerId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F8A600" />
          <stop offset="1" stopColor="#DA7400" />
        </linearGradient>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        <rect width="24" height="24" fill={`url(#${beerId})`} />

        {BUBBLES.map((bubble) => (
          <circle
            key={`${bubble.cx}-${bubble.delay}`}
            className="orb-bubble"
            cx={bubble.cx}
            cy={bubble.cy}
            r={bubble.r}
            fill="#fff4d6"
            style={{ animationDelay: bubble.delay }}
          />
        ))}

        <g className="orb-foam">
          <path className="orb-foam-wave" d={ORB_HEAD_PATH} fill="#fbf1d6" />
        </g>
      </g>
    </svg>
  );
}
